<script setup lang="ts">
import { useDowntimes } from '@/composables/eos/downtime'
import { NotifyFail } from '@/helpers/core/notifications'
import { getClientDetailedDowntimeSummary } from '@/services/eos/downtimes'
import type { IClientDetailedDowntimeSummary, IClientDowntimeSummary } from '@/types/eos/DowntimeEvent'
import type { IClientDowntimeSummaryFilters, IDowntimeClientDetailSummaryFilters } from '@/types/eos/filters'
import { AdminPortalAPIError } from '@/types/shared/errors'
import { formatPercentage, tableColumnPercentageWithTwoDecimals } from '@/util/formatters'
import { rangeToISOInTimezone } from '@/util/datetime'
import RlTimezoneSelect from '@/components/common/RlTimezoneSelect.vue'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { DateTime } from 'luxon'
import { computed, reactive, ref, watch } from 'vue'

const monthStartDateTime = DateTime.now().startOf('month')
const monthEndDateTime = DateTime.now().startOf('month').plus({ month: 1 })

const filters = reactive<IClientDowntimeSummaryFilters>({
  name: undefined,
  start_datetime: monthStartDateTime.toISO(),
  end_datetime: monthEndDateTime.toISO(),
})
const dateRangeFilter = ref<[Date, Date]>([monthStartDateTime.toJSDate(), monthEndDateTime.toJSDate()])
const timezoneFilter = ref(DateTime.now().zoneName)

const { getClientsDowntimeSummaryQuery } = useDowntimes()

const { isFetching, isError, isRefetching, isPending, data, error, refetch } = getClientsDowntimeSummaryQuery(filters)

const dateRangeISO = computed(() => {
  const range = dateRangeFilter.value
  if (Array.isArray(range) && range[0] instanceof Date && range[1] instanceof Date) {
    return rangeToISOInTimezone([range[0], range[1]], timezoneFilter.value)
  }
  return null
})

watch(
  dateRangeISO,
  (iso) => {
    if (iso) {
      filters.start_datetime = iso.startISO
      filters.end_datetime = iso.endISO
    }
  },
  { immediate: true }
)

const resetDateRange = (newRange: Array<Date> | null) => {
  if (!newRange || newRange.length < 2) {
    dateRangeFilter.value = [monthStartDateTime.toJSDate(), monthEndDateTime.toJSDate()]
  }

  // Clear cached expanded data so it is refetched for the new date range
  expandedClientData.value = {}
  expandedLoading.value = {}
}

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

// Manage table expansion to fetch hosting client miners downtime summaries
const expandedClientData = ref<Record<string, IClientDetailedDowntimeSummary[]>>({})
const expandedLoading = ref<Record<string, boolean>>({})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleTableExpand = async (row: IClientDowntimeSummary, _: IClientDowntimeSummary[] | boolean) => {
  if (!expandedClientData.value[row.id]) {
    expandedLoading.value[row.id] = true
    try {
      const filt: IDowntimeClientDetailSummaryFilters = {
        start_datetime: filters.start_datetime,
        end_datetime: filters.end_datetime,
      }
      const response = await getClientDetailedDowntimeSummary(row.id, filt)
      expandedClientData.value[row.id] = response
    } catch (error: unknown) {
      if (error instanceof AdminPortalAPIError) {
        NotifyFail(error.description)
      } else {
        NotifyFail('Failed to fetch client downtime summary details')
      }
    } finally {
      expandedLoading.value[row.id] = false
    }
  }
}

const minerTypeLabels: { key: keyof IClientDowntimeSummary; label: string }[] = [
  { key: 'total_registered_miners', label: 'Total registered' },
  { key: 'online_miners', label: 'Online' },
  { key: 'offline_miners', label: 'Offline' },
  { key: 'pending_miners', label: 'Pending' },
  { key: 'disabled_miners', label: 'Disabled' },
  { key: 'hashing_miners', label: 'Hashing' },
  { key: 'sleeping_miners', label: 'Sleeping' },
  { key: 'deployed_miners', label: 'Deployed' },
  { key: 'in_repair_miners', label: 'In repair' },
]
</script>

<template>
  <ViewLayout title="Clients Downtime Summary">
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex gap-2">
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
        <div class="flex items-center gap-2 flex-nowrap">
          <el-date-picker
            v-model="dateRangeFilter"
            :disabled="isPending"
            type="datetimerange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="YYYY-MM-DD HH:mm:ss"
            date-format="YYYY/MM/DD ddd"
            time-format="A hh:mm:ss"
            editable
            :clearable="false"
            :shortcuts="[
              {
                text: 'Current Month',
                value: () => {
                  const now = new Date()
                  const start = new Date(now.getFullYear(), now.getMonth(), 1)
                  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                  return [start, end]
                },
              },
              {
                text: 'Last Month',
                value: () => {
                  const now = new Date()
                  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  const end = new Date(now.getFullYear(), now.getMonth(), 1)
                  return [start, end]
                },
              },
            ]"
            @change="resetDateRange"
          />
          <RlTimezoneSelect class="w-56" v-model="timezoneFilter" :disabled="isPending" />
        </div>
      </div>
      <el-table v-loading="isFetching" :data="data" empty-text="No data" stripe @expand-change="handleTableExpand">
        <el-table-column type="expand">
          <template #default="scope">
            <el-table
              v-loading="expandedLoading[scope.row.id]"
              :data="expandedClientData[scope.row.id]"
              empty-text="No data"
              stripe
              size="small"
            >
              <el-table-column label="Operational Miners" header-align="center">
                <el-table-column prop="model" label="Model" :min-width="196" />
                <el-table-column prop="quantity" label="Quantity" :min-width="80" />
                <el-table-column prop="general_downtime_minutes" label="General Downtime Minutes" :min-width="120" />
                <el-table-column prop="derack_downtime_minutes" label="Derack Downtime Minutes" :min-width="120" />
                <el-table-column
                  prop="period_uptime_percentage"
                  label="Period Uptime"
                  :min-width="120"
                  :formatter="tableColumnPercentageWithTwoDecimals"
                />
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="Client" :min-width="120">
          <template #default="scope">
            <el-tooltip v-if="scope.row.foreman_id ?? scope.row.foreman_name" placement="top">
              <template #content>
                <p v-if="scope.row.foreman_id">Foreman ID: {{ scope.row.foreman_id }}</p>
                <p v-if="scope.row.foreman_name">Foreman Client Name: {{ scope.row.foreman_name }}</p>
              </template>
              <span class="cursor-default">{{ scope.row.name }}</span>
            </el-tooltip>
            <span v-else>{{ scope.row.name }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="miners_under_contract" :min-width="120">
          <template #header>
            <div class="flex items-center gap-1">
              <span>Miners Under Contract</span>
              <el-tooltip
                content="Amount of miners that are currently under contract with the client. Used to calculate the period uptime."
                placement="top"
              >
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Miners" :min-width="320">
          <template #header>
            <div class="flex items-center gap-1">
              <span>Miners</span>
              <el-tooltip placement="top">
                <template #content>
                  <div><strong>Total registered:</strong> Miners registered in the system</div>
                  <div><strong>Online / Offline:</strong> Connection status</div>
                  <div><strong>Pending / Disabled:</strong> Provisioning or disabled state</div>
                  <div><strong>Hashing:</strong> Miners that registered a hashrate in the last hour</div>
                  <div><strong>Sleeping / Deployed / In repair:</strong> Operational state</div>
                </template>
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
          <template #default="scope">
            <div class="grid grid-cols-3 gap-y-1 gap-x-4 text-sm leading-6">
              <div v-for="item in minerTypeLabels" :key="item.key" class="flex gap-1.5 items-baseline">
                <span class="shrink-0 text-text-tertiary">{{ item.label }}:</span>
                <span class="tabular-nums" :class="{ 'text-text-tertiary': scope.row[item.key] == null }">
                  {{ scope.row[item.key] ?? '–' }}
                </span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="uptime_threshold_percentage"
          label="Uptime Threshold"
          :min-width="100"
          :formatter="tableColumnPercentageWithTwoDecimals"
        />
        <el-table-column prop="period_downtime_minutes" label="Period Downtime" :min-width="140">
          <template #default="scope">
            <el-tooltip placement="top">
              <template #content> {{ scope.row.period_downtime_minutes ?? 0 }} minutes </template>
              <span>{{ Math.round((scope.row.period_downtime_minutes ?? 0) / 60).toLocaleString('de-DE') }} hs</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="tracked_miners_period_uptime_percentage" label="Period Uptime" :min-width="140">
          <template #header>
            <div class="flex items-center gap-1">
              <span>Period Uptime</span>
              <el-tooltip
                content="Uptime determined by the amount of online + offline miners and the total downtime time in the period"
                placement="top"
              >
                <Icon
                  icon="material-symbols:help-outline"
                  class="text-gray-400 hover:text-gray-600 cursor-help"
                  height="16"
                  width="16"
                />
              </el-tooltip>
            </div>
          </template>
          <template #default="scope">
            <span
              :class="{
                'text-red-500':
                  scope.row.tracked_miners_period_uptime_percentage < scope.row.uptime_threshold_percentage,
                'text-emerald-500':
                  scope.row.tracked_miners_period_uptime_percentage >= scope.row.uptime_threshold_percentage,
              }"
            >
              {{ formatPercentage(scope.row.tracked_miners_period_uptime_percentage, 2) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </ViewLayout>
</template>
